import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, Upload } from "lucide-react";
import { uploadToMediaLibrary, type MediaItem } from "@/cms/lib/mediaLibrary";
import { toast } from "@/hooks/use-toast";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSelect: (item: MediaItem) => void;
  /** Voreingestellter Ordner-Filter, z. B. "popups" */
  defaultFolder?: string;
};

export function MediaPicker({ open, onOpenChange, onSelect, defaultFolder = "" }: Props) {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState<string>(defaultFolder);
  const [tag, setTag] = useState<string>("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (open) load(); }, [open]);

  async function load() {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("cms_media").select("*").order("created_at", { ascending: false }).limit(500);
    if (error) toast({ title: "Fehler", description: error.message, variant: "destructive" });
    setItems((data as MediaItem[]) ?? []);
    setLoading(false);
  }

  const folders = useMemo(() => {
    const s = new Set<string>();
    items.forEach((i) => i.folder && s.add(i.folder));
    return Array.from(s).sort();
  }, [items]);

  const tags = useMemo(() => {
    const s = new Set<string>();
    items.forEach((i) => i.tags.forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase().trim();
    return items.filter((i) => {
      if (folder && i.folder !== folder) return false;
      if (tag && !i.tags.includes(tag)) return false;
      if (!ql) return true;
      return (
        i.filename.toLowerCase().includes(ql) ||
        i.title.toLowerCase().includes(ql) ||
        i.alt_text.toLowerCase().includes(ql) ||
        i.tags.some((t) => t.includes(ql))
      );
    });
  }, [items, q, folder, tag]);

  async function handleUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        await uploadToMediaLibrary(f, { folder });
      }
      toast({ title: "Hochgeladen", description: "Metadaten werden im Hintergrund generiert." });
      await load();
    } catch (e: any) {
      toast({ title: "Upload-Fehler", description: e.message, variant: "destructive" });
    } finally { setUploading(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Aus Mediathek wählen</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="pl-9" />
          </div>
          <Select value={folder || "__all"} onValueChange={(v) => setFolder(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Alle Ordner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Alle Ordner</SelectItem>
              {folders.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tag || "__all"} onValueChange={(v) => setTag(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Alle Tags" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Alle Tags</SelectItem>
              {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="inline-flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-muted text-sm">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            <span>{uploading ? "Lade hoch…" : "Hochladen"}</span>
            <input type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => handleUpload(e.target.files)} />
          </label>
        </div>

        <div className="flex-1 overflow-auto -mx-2 px-2">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Lade…</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">Keine Bilder gefunden.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 pt-3">
              {filtered.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => { onSelect(it); onOpenChange(false); }}
                  className="group text-left rounded-lg border overflow-hidden hover:border-[#6C184F] transition-colors bg-card"
                >
                  <div className="aspect-square bg-muted overflow-hidden">
                    <img src={it.url} alt={it.alt_text} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                  </div>
                  <div className="p-2 space-y-1">
                    <div className="text-xs font-medium truncate">{it.title || it.filename}</div>
                    {it.folder && <Badge variant="outline" className="text-[10px]">{it.folder}</Badge>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
